<template>
  <div>
    <div class="usage-tracker">
      <div class="d-flex align-items-center">
        <div
          class="d-flex gap-2 justify-content-center w-100 align-items-center"
        >
          <h3 class="tracker-title mb-0">Usage Tracker</h3>
          <Tooltip
            :help-text="'Individual subscriptions are now linked to usage. Please refer to our Individual Subscription FAQ in the Quick Links section.'"
            :help-text-aria="'Individual subscriptions are now linked to usage. Please refer to our Individual Subscription FAQ in the Quick Links section.'"
            help-id="usage-tracker"
            label="Usage Tracker"
          />
        </div>
      </div>
      <div v-if="trackerData != null" class="tracker-content">
        <div class="units-bar">
          <div class="units-label">
            <span>Remaining Units</span>
            <span class="units-value"
              >{{ trackerData.remaining_units }} /
              {{ trackerData.total_units }}</span
            >
          </div>
          <b-progress
            :value="usagePercentage"
            :variant="progressVariant"
          ></b-progress>
        </div>
        <div class="tracker-details">
          <div class="dates-section">
            <div v-if="formattedExpiryDate" class="date-card">
              <div class="date-icon-wrapper">
                <i class="fas fa-calendar-alt" aria-hidden="true"></i>
              </div>
              <div class="date-content">
                <span class="date-label">Subscribed Units expiring</span>
                <span class="date-value">{{ formattedExpiryDate }}</span>
              </div>
            </div>
            <div v-if="formattedFreeExpiryDate" class="date-card">
              <div class="date-icon-wrapper success-icon">
                <i class="fas fa-gift" aria-hidden="true"></i>
              </div>
              <div class="date-content">
                <span class="date-label">50 Complimentary Units expiring</span>
                <span class="date-value">{{ formattedFreeExpiryDate }}</span>
              </div>
            </div>
            <div v-if="formattedAnnualDiscountAppliedDate" class="date-card">
              <div class="date-icon-wrapper">
                <i class="fas fa-tag" aria-hidden="true"></i>
              </div>
              <div class="date-content">
                <span class="date-label"
                  >Yearly discount for 300 units applied on</span
                >
                <span class="date-value">{{
                  formattedAnnualDiscountAppliedDate
                }}</span>
              </div>
            </div>
            <div v-if="formattedNextAnnualDiscountDate" class="date-card">
              <div class="date-icon-wrapper">
                <i class="fas fa-redo" aria-hidden="true"></i>
              </div>
              <div class="date-content">
                <span class="date-label">Next yearly discount available on</span>
                <span class="date-value">{{
                  formattedNextAnnualDiscountDate
                }}</span>
              </div>
            </div>
          </div>
          <div v-if="annualDiscountEligibilityMessage" class="discount-message">
            <i class="fas fa-info-circle" aria-hidden="true"></i>
            <span>{{ annualDiscountEligibilityMessage }}</span>
          </div>
          <div class="actions-section">
            <a
              class="btn btn-primary"
              :href="bucketPurchaseLink"
              target="_blank"
              aria-label="Add Usage Units"
            >
              Add Usage Units
            </a>
            <div v-if="trackerData.bucketsBought" class="detail-item">
              <i class="fas fa-box" aria-hidden="true"></i>
              <span>Bundles Bought: {{ trackerData.bucketsBought }}</span>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="tracker-content">
        <div v-if="!isLoading" class="no-units-section">
          <div class="no-units-content">
            <i class="fas fa-box-open" aria-hidden="true"></i>
            <h4>No Usage Units Available</h4>
            <p>Get started by adding usage units to your account</p>
            <a
              class="btn btn-primary"
              :href="bucketPurchaseLink"
              target="_blank"
              aria-label="Buy Usage Units"
            >
              Buy Usage Units
            </a>
          </div>
        </div>
        <div v-else class="loading-section">
          <b-spinner></b-spinner>
          <span>Loading Usage Tracker...</span>
        </div>
      </div>
    </div>
    <div>
      <div class="usage-tracker">
        <h3 class="tracker-title">Activation Code</h3>
        <b-button variant="secondary" @click="showActivation = !showActivation">
          {{ showActivation ? "Hide" : "Enter Activation Code" }}
        </b-button>
        <!-- New inline activation section -->
        <b-collapse
          id="activation-section"
          v-model="showActivation"
          class="mt-3"
        >
          <b-card>
            <h4>Redeem your activation code</h4>
            <b-form @submit.prevent="activateSubscription">
              <b-form-group>
                <b-form-input
                  id="activation-code"
                  v-model="activationCode"
                  :state="activationCodeState"
                  required
                  placeholder="Enter your activation code"
                ></b-form-input>
              </b-form-group>
              <b-button
                type="submit"
                variant="primary"
                :disabled="isActivating"
              >
                <b-spinner small v-if="isActivating"></b-spinner>
                {{ isActivating ? "Activating..." : "Activate" }}
              </b-button>
            </b-form>
            <b-alert v-if="activationError" show variant="danger" class="mt-3">
              {{ activationError }}
            </b-alert>
            <b-alert
              v-if="activationSuccess"
              show
              variant="primary"
              class="mt-3"
            >
              {{ activationSuccess }}
            </b-alert>
          </b-card>
        </b-collapse>
      </div>
    </div>
  </div>
</template>

<script>
import Api from "@/services/Api";
import Tooltip from "@/components/common/Tooltip.vue";

export default {
  name: "UsageTracker",
  components: {
    Tooltip,
  },
  props: {
    trackerData: {
      type: [Object, null],
      default: null,
    },
    bucketPurchaseLink: {
      type: String,
      default: null,
    },
    partyId: {
      type: String,
      default: null,
    },
    onFetchTrackerData: {
      type: Function,
      required: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    usagePercentage() {
      if (this.trackerData.remaining_units == this.trackerData.total_units)
        return 100;
      return (
        (this.trackerData.remaining_units / this.trackerData.total_units) * 100
      );
    },
    progressVariant() {
      if (this.usagePercentage > 70) return "success";
      if (this.usagePercentage > 30) return "warning";
      return "danger";
    },
    formattedExpiryDate() {
      return this.formatDisplayDate(this.trackerData.expiry_date);
    },
    formattedFreeExpiryDate() {
      return this.formatDisplayDate(this.trackerData.free_expiry_date);
    },
    formattedAnnualDiscountAppliedDate() {
      return this.formatDisplayDate(this.trackerData.first_annual_purchase_date);
    },
    formattedNextAnnualDiscountDate() {
      if (!this.trackerData.first_annual_purchase_date) return null;
      const appliedDate = new Date(this.trackerData.first_annual_purchase_date);
      if (Number.isNaN(appliedDate.getTime())) return null;
      const nextDate = new Date(appliedDate);
      nextDate.setFullYear(nextDate.getFullYear() + 1);
      return this.formatDisplayDate(nextDate);
    },
    annualDiscountEligibilityMessage() {
      if (!this.trackerData) return null;
      const discountPercentage = Number(this.trackerData.discountPercentage);
      if (Number.isNaN(discountPercentage)) return null;
      if (discountPercentage > 0) {
        return "You are eligible for the annual 300-unit discount ($200).";
      }
      return null;
    },
    activationCodeState() {
      return this.activationCode.length > 0 ? null : false;
    },
  },
  data() {
    return {
      activationCode: "",
      showActivation: false,
      isActivating: false,
      activationError: null,
      activationSuccess: null,
    };
  },
  methods: {
    formatDisplayDate(value) {
      if (!value) return null;
      const dateValue = value instanceof Date ? value : new Date(value);
      if (Number.isNaN(dateValue.getTime())) return null;
      return dateValue.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
    async activateSubscription() {
      if (!this.activationCode) {
        this.activationError = "Please enter a valid activation code.";
        return;
      }

      this.isActivating = true;
      this.activationError = null;

      try {
        if (!this.partyId) {
          this.activationError = "Error during activation.";
          this.isActivating = false;
          console.error("partyId is not set properly");
        }
        const response = await Api().post("/subscription/activate/bucket", {
          activationCode: this.activationCode,
          partyId: this.partyId,
        });
        if (response.status && response.status == 200) {
          this.activationSuccess = "Activation Code applied successfully!";
          this.isActivating = false;
          this.activationError = "";
          this.activationCode = "";
          await this.onFetchTrackerData();
        } else {
          throw new Error(response.data.message || "Activation failed");
        }
      } catch (error) {
        // console.log(error);
        if (error.response) {
          this.activationError =
            error.response.data.message ||
            "An error occurred during activation.";
        } else {
          this.activationError =
            error.message ||
            "An error occurred while activating the subscription.";
        }
      } finally {
        this.isActivating = false;
      }
    },
  },
};
</script>

<style scoped>
.usage-tracker {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.tracker-title {
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: var(--primary-color);
}

.tracker-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.units-bar {
  background-color: white;
  padding: 1rem;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.units-label {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  font-weight: bold;
}

.units-value {
  color: var(--primary-color);
}

.tracker-details {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.dates-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
}

.date-card {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 1rem;
  background-color: white;
  padding: 1rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  border: 1px solid #eee;
  transition: all 0.2s ease;
}

.date-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  border-color: #ddd;
}

.date-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background-color: #eef2f6;
  color: var(--primary-color);
  font-size: 1.1rem;
  flex-shrink: 0;
}

.date-icon-wrapper.success-icon {
  background-color: #e6f4ea;
  color: #28a745;
}

.date-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  gap: 0.15rem;
  overflow: hidden;
}

.date-label {
  font-size: 0.8rem;
  color: #6c757d;
  font-weight: 500;
  line-height: 1.2;
}

.date-value {
  font-size: 0.95rem;
  color: #2c3e50;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.actions-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
}

.discount-message {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: #eef2f6;
  color: #0c335d;
  border: 1px solid #d8e2ee;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  font-size: 0.95rem;
}

.buy-units-btn {
  color: white !important;
  min-width: 200px;
  padding: 0.5rem 1rem;
  font-weight: 500;
  transition: transform 0.2s ease;
  background-color: var(--primary-color);
  border-color: var(--primary-color);
}

.buy-units-btn:hover {
  transform: translateY(-2px);
  background-color: var(--primary-color);
  border-color: var(--primary-color);
  opacity: 0.9;
}

.detail-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: white;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  width: 100%;
  justify-content: center;
}

.detail-item i {
  color: var(--primary-color);
}


.tooltip-link {
  color: #4caf50;
  text-decoration: underline;
  cursor: pointer;
}

.tooltip-link:hover {
  color: #45a049;
}

.no-units-section {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.no-units-content {
  text-align: center;
  padding: 2rem;
}

.no-units-content i {
  font-size: 3rem;
  color: #dc3545;
  margin-bottom: 1rem;
  opacity: 0.8;
}

.no-units-content h4 {
  color: #333;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.no-units-content p {
  color: #666;
  margin-bottom: 1.5rem;
  font-size: 0.95rem;
}

.add-units-btn {
  color: white !important;
  min-width: 200px;
  padding: 0.75rem 1.5rem;
  font-weight: 500;
  transition: transform 0.2s ease;
}

.add-units-btn:hover {
  transform: translateY(-2px);
}

.loading-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  gap: 1rem;
  color: #666;
}

.loading-section .spinner-border {
  width: 2rem;
  height: 2rem;
}
</style>
